import React, { useState } from 'react';
import './forgotPassword.css';
import { useAlert } from '../ui/alertProvider';
import AuthContext from '../../store/authContext';
import { useResources, useComponentResources } from '../../store/resourcesProvider';
import ApiClient from '../../Services/ApiClient';
import { SiteSettings } from '../../../Models/SiteSettings';
import { validateName, validateEmail } from '../../Utils/validation';
import ConfirmVerification from './confirmVerification';

interface RegisterProps {
  onRegisterSuccess?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onRegisterSuccess }) => {
  const [username, setUsername] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('Regular');
  const [accountType, setAccountType] = useState<'patient' | 'manager'>('patient');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ username?: string; email?: string; password?: string; phone?: string; role?: string }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [regAvailability, setRegAvailability] = useState<{ canRegister: boolean; remaining?: number | null; reason?: string | null } | null>(null);
  const { showAlert } = useAlert();
  const auth = React.useContext(AuthContext);
  const resources = useResources();
  const pageRes = useComponentResources('Register', ['Register.Title','Register.UsernameLabel','Register.EmailLabel','Register.PhoneLabel','Register.PasswordLabel','Register.ConfirmPasswordLabel','Register.Submit','Register.PasswordMismatch','Register.PatientRegistrationDisabled','Register.SiteLimitReached','Register.ManagerNotAllowed']);

  const siteDisabledForUsers = !!(siteSettings && (siteSettings.canRegisterNewUsers === false || siteSettings.enableUsersLogin === false));
  const reachedMaxUsers = !!(siteSettings && siteSettings.canRegisterNewUsers === true && regAvailability && regAvailability.remaining === 0 && typeof siteSettings.maxRegisteredSiteUsers === 'number' && siteSettings.maxRegisteredSiteUsers > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Quick client-side uniqueness checks before doing heavy server-side operations
    try {
      // check email
      if (userEmail && userEmail.length > 0) {
        const res: any = await ApiClient.getWithParams('/auth/exists/email', { email: userEmail });
        if (res?.exists) {
          setFieldErrors(prev => ({ ...prev, email: 'האימייל כבר רשום במערכת' }));
          showAlert('האימייל כבר רשום במערכת', 'error');
          return;
        }
      }
      // check username
      if (username && username.length > 0) {
        const res2: any = await ApiClient.getWithParams('/auth/exists/username', { username });
        if (res2?.exists) {
          setFieldErrors(prev => ({ ...prev, username: 'שם משתמש כבר קיים' }));
          showAlert('שם משתמש כבר קיים', 'error');
          return;
        }
      }
    } catch (err) {
      // ignore check failures - server-side will still enforce uniqueness
      console.warn('Uniqueness checks failed', err);
    }

    // helper: map server validation errors to form fields. returns true when mapped
    const applyServerErrors = (server: any) => {
      if (!server) return false;
      const errs = server.errors || server.error || server;
      if (!errs || typeof errs !== 'object') return false;
      const newFieldErrors: any = {};
      const pick = (keys: string[]) => {
        for (const k of keys) {
          if (Object.prototype.hasOwnProperty.call(errs, k) && errs[k]) return errs[k];
        }
        return null;
      };
      const emailErr = pick(['email', 'Email', 'UserEmail', 'userEmail']);
      const nameErr = pick(['name', 'Name', 'username', 'userName']);
      const passwordErr = pick(['password', 'Password']);
      if (emailErr) newFieldErrors.email = Array.isArray(emailErr) ? emailErr.join(' ') : String(emailErr);
      if (nameErr) newFieldErrors.username = Array.isArray(nameErr) ? nameErr.join(' ') : String(nameErr);
      if (passwordErr) newFieldErrors.password = Array.isArray(passwordErr) ? passwordErr.join(' ') : String(passwordErr);
      // if we mapped any field errors, set them and return true
      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
        return true;
      }
      return false;
    };

    // Only check registration availability for patient registrations
    if (accountType === 'patient') {
      try {
        // Check explicit site settings first
        const settings: SiteSettings = await ApiClient.get('/settings/site/current/settings');
        if (settings && settings.enableUsersLogin === false) {
          showAlert(pageRes['Register.PatientRegistrationDisabled'] ?? resources.get('Register.PatientRegistrationDisabled','הרשמה למטופלים אינה זמינה על ידי מנהל האתר'), 'error');
          return;
        }
        // if site has explicit patient toggle, use that; otherwise fall back to canRegisterNewUsers
        if (settings && (settings.canRegisterNewPatients === false || settings.canRegisterNewUsers === false)) {
          showAlert(pageRes['Register.PatientRegistrationDisabled'] ?? resources.get('Register.PatientRegistrationDisabled','הרשמה למטופלים אינה זמינה על ידי מנהל האתר'), 'error');
          return;
        }

        // If a max is configured, consult availability to see remaining slots
        if (settings && typeof settings.maxRegisteredSiteUsers === 'number' && settings.maxRegisteredSiteUsers > 0) {
          try {
            const avail: any = await ApiClient.get('/settings/site/current/registration-availability');
            if (avail && (avail.canRegister === false || avail.remaining === 0)) {
              // determine reason and show specific alert
              if (avail.reason === 'registration_disabled') {
                showAlert(pageRes['Register.PatientRegistrationDisabled'] ?? resources.get('Register.PatientRegistrationDisabled','הרשמה למטופלים אינה זמינה על ידי מנהל האתר'), 'error');
              } else if (avail.remaining === 0) {
                showAlert(pageRes['Register.SiteLimitReached'] ?? resources.get('Register.SiteLimitReached','Patient registration is not available: site has reached the registered users limit'), 'error');
              } else {
                showAlert(pageRes['Register.SiteLimitReached'] ?? resources.get('Register.SiteLimitReached','Patient registration is not available at this time'), 'error');
              }
              return;
            }
          } catch (err) {
            console.warn('Failed to check registration availability', err);
            // Be conservative and block registration if we couldn't determine availability for a limited site
            if (settings && settings.maxRegisteredSiteUsers > 0) {
              showAlert(pageRes['Register.SiteLimitReached'] ?? resources.get('Register.SiteLimitReached','Patient registration is not available at this time'), 'error');
              return;
            }
          }
        }
      } catch (err) {
        // If settings check fails, continue but warn in console
        console.warn('Failed to check site settings/availability', err);
      }
    } else {
      // Manager/staff registration: send proposal to superadmins for approval when site allows special user registrations
      try {
        const settings: SiteSettings = await ApiClient.get('/settings/site/current/settings');
        // Only allow propose flow when site allows adding users via admin approval
        if (settings && settings.canRegisterNewUsers === true) {
          // check availability if a max is configured
            if (typeof settings.maxRegisteredSiteUsers === 'number' && settings.maxRegisteredSiteUsers > 0) {
            try {
              // Use settings endpoint and request manager availability explicitly
              const avail: any = await ApiClient.get('/api/settings/site/current/registration-availability?type=manager');
              // if availability explicitly forbids registration, block
              if (avail && avail.canRegister === false) {
                showAlert(pageRes['Register.PatientRegistrationDisabled'] ?? resources.get('Register.PatientRegistrationDisabled','הרשמה למטופלים אינה זמינה על ידי מנהל האתר'), 'error');
                return;
              }
            } catch (err) {
              console.warn('Failed to check registration availability for special user proposal', err);
            }
          }

          // For manager/staff, propose: send email to superadmins (existing flow) but do not request verification code
          const proposal: any = {
            Email: userEmail,
            Name: username,
            Password: password,
            UserRole: role || 'Regular',
            SiteId: resources?.siteId ?? null,
            UserPhone: phone || null,
            NotifyEmail: null
          };
          await ApiClient.post('/api/admin/propose-user', proposal);
          showAlert('בקשת רישום נשלחה למנהלי המערכת לאישור', 'success');
          return;
        }
        else {
          showAlert(pageRes['Register.ManagerNotAllowed'] ?? resources.get('Register.ManagerNotAllowed','Manager/staff accounts cannot be registered using this form. Contact an administrator.'), 'error');
          return;
        }
      } catch (err) {
        showAlert(pageRes['Register.ManagerNotAllowed'] ?? resources.get('Register.ManagerNotAllowed','Manager/staff accounts cannot be registered using this form. Contact an administrator.'), 'error');
        return;
      }
    }

    if (password !== confirmPassword) {
      setError('הסיסמאות לא תואמות');
      return;
    }

    // field validations
    const nameErr = validateName(username);
    const emailErr = validateEmail(userEmail);
    const passErr = password.length < 6 ? 'Password must be at least 6 characters' : null;
    // optional phone validation (if provided)
    const phoneErr = phone && phone.trim().length > 0 && phone.trim().length < 6 ? 'מספר טלפון קצר מדי' : null;
    setFieldErrors({ username: nameErr ?? undefined, email: emailErr ?? undefined, password: passErr ?? undefined, phone: phoneErr ?? undefined });
    if (nameErr || emailErr || passErr) {
      setError('אנא בדוק את השדות');
      return;
    }

    if (accountType === 'patient') {
      // Patient flow: send verification code to patient's email and require code input to complete.
      setLoading(true);
      try {
        const payload: any = { Email: userEmail, Name: username, Phone: phone || null, UserRole: 'Patient' };
        await ApiClient.post('/auth/request-verification', payload);
        setPendingPayload({ Name: username, UserEmail: userEmail, Password: password, Phone: phone || null, UserRole: 'Patient' });
        setSubmitted(true);
        showAlert('קוד אימות נשלח לכתובת המייל', 'success');
      } catch (err: any) {
        // Prefer server-provided structured error messages when available
        const server = err?.data;
        const mapped = applyServerErrors(server);
        if (!mapped) {
          const msg = (server && (server?.failureReason || server?.message)) || (typeof server === 'string' ? server : null) || err?.message || 'כשלון בשליחת קוד, אנא נסה שוב.';
          showAlert(msg, 'error');
        }
      } finally { setLoading(false); }
    } else {
      // Manager/staff: propose user for superadmin approval (no verification code)
      setLoading(true);
      try {
        const proposal: any = {
          Email: userEmail,
          Name: username,
          Password: password,
          UserRole: role || 'Regular',
          SiteId: resources?.siteId ?? null,
          UserPhone: phone || null,
          NotifyEmail: null
        };
        await ApiClient.post('/api/admin/propose-user', proposal);
        showAlert('בקשת רישום נשלחה למנהלי המערכת לאישור', 'success');
      } catch (err: any) {
        const server = err?.data;
        const mapped = applyServerErrors(server);
        if (!mapped) {
          const msg = (server && (server?.failureReason || server?.message)) || (typeof server === 'string' ? server : null) || err?.message || 'כשלון בשליחת בקשת רישום, אנא נסה שוב.';
          showAlert(msg, 'error');
        }
      } finally { setLoading(false); }
      return;
    }
     };

  // load site settings on mount so UI can reflect login/registration toggles
  React.useEffect(() => {
    // read account type from AuthContext (set by login page)
    try {
      const t = auth?.registrationAccountType as 'patient' | 'manager' | undefined;
      if (t === 'manager' || t === 'patient') setAccountType(t);
    } catch {}
    const load = async () => {
      try {
        const s: SiteSettings = await ApiClient.get('/settings/site/current/settings');
        setSiteSettings(s ?? null);
      } catch (e) {
        setSiteSettings(null);
      }
    };
    load();
    // also load registration availability so UI can show future availability messages
    const loadAvail = async () => {
      try {
        const a: any = await ApiClient.get('/settings/site/current/registration-availability');
        setRegAvailability(a ?? null);
      } catch { setRegAvailability(null); }
    };
    loadAvail();
  }, []);

  // Title should reflect the account type (patient or manager)
  const accountTitle = accountType === 'patient'
    ? (pageRes['Register.TitlePatient'] ?? resources.get('Register.TitlePatient','הרשמת מטופל'))
    : (pageRes['Register.TitleManager'] ?? resources.get('Register.TitleManager','הרשמת משתמש'));

  return (
    <div className="forgot-password-container page-padding">
      <h2>{accountTitle}</h2>
      {submitted ? (
        // Show verification input and handle final registration upon success
        <div>
          <div className="success-message">קוד אימות נשלח. בדוק את המייל והזן את הקוד להשלמת הרשמה.</div>
          {pendingPayload && <ConfirmVerification email={pendingPayload.UserEmail} onSuccess={async (code: string) => {
            try {
              setLoading(true);
              // Re-check site settings before finalizing registration
              try {
                const s: SiteSettings = await ApiClient.get('/settings/site/current/settings');
                if (s && (s.canRegisterNewUsers === false || s.enableUsersLogin === false)) {
                  showAlert('הרשמות הושבתו על ידי מנהל המערכת', 'error');
                  return;
                }
              } catch (err) {
                // if settings cannot be retrieved, be conservative and block registration when site has limits
                console.warn('Failed to re-check site settings before finalize registration', err);
              }

              // include verification code in the final register call
              const body = { UserEmail: pendingPayload.UserEmail, Name: pendingPayload.Name, Password: pendingPayload.Password, Phone: pendingPayload.Phone, UserRole: pendingPayload.UserRole, Code: code };
              await ApiClient.post('/auth/register', body);
              showAlert('ההרשמה הושלמה בהצלחה', 'success');
              // Do not trigger parent redirect here; keep user on the registration/confirmation page
              // clear state
              setPendingPayload(null);
              setSubmitted(false);
            } catch (e: any) {
              const srv = e?.data;
              const mapped = applyServerErrors(srv);
              if (!mapped) {
                const message = (srv && (srv?.failureReason || srv?.message)) || (typeof srv === 'string' ? srv : null) || e?.message || 'כשלון בהרשמה, אנא נסה שוב.';
                showAlert(message, 'error');
              } else {
                // if field errors were set, show a short bubble informing user to correct form
                showAlert('אנא תקן שגיאות בטופס לפני המשך', 'error');
              }
            } finally { setLoading(false); }
          }} />}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="register-username" className="">{pageRes['Register.UsernameLabel'] ?? resources.get('Register.UsernameLabel',':שם משתמש')}</label>
            <input id="register-username" type="text" value={username} onChange={e => { setUsername(e.target.value); const err = validateName(e.target.value); setFieldErrors(prev => ({ ...prev, username: err ?? undefined })); }} required aria-describedby={fieldErrors.username ? 'register-username-error' : undefined} aria-invalid={!!fieldErrors.username} />
            {fieldErrors.username ? <div id="register-username-error" className="error-message">{fieldErrors.username}</div> : null}
          </div>
          <div className="form-group">
            <label htmlFor="register-email">{pageRes['Register.EmailLabel'] ?? resources.get('Register.EmailLabel','אימייל:')}</label>
            <input id="register-email" type="email" value={userEmail} onChange={e => { setUserEmail(e.target.value); const err = validateEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: err ?? undefined })); }} required aria-describedby={fieldErrors.email ? 'register-email-error' : undefined} aria-invalid={!!fieldErrors.email} />
            {fieldErrors.email ? <div id="register-email-error" className="error-message">{fieldErrors.email}</div> : null}
          </div>
          <div className="form-group">
            <label htmlFor="register-phone">{pageRes['Register.PhoneLabel'] ?? resources.get('Register.PhoneLabel','טלפון (אופציונלי):')}</label>
            <input id="register-phone" type="text" value={phone} onChange={e => {
              const v = e.target.value;
              setPhone(v);
              const err = v && v.trim().length > 0 && v.trim().length < 6 ? 'מספר טלפון קצר מדי' : undefined;
              setFieldErrors(prev => ({ ...prev, phone: err }));
            }} aria-describedby={fieldErrors.phone ? 'register-phone-error' : undefined} aria-invalid={!!fieldErrors.phone} />
            {fieldErrors.phone ? <div id="register-phone-error" className="error-message">{fieldErrors.phone}</div> : null}
          </div>
          {/* accountType is determined on the login page and passed via query string; no radio input here */}
          <div className="form-group">
            <label htmlFor="register-password">{pageRes['Register.PasswordLabel'] ?? resources.get('Register.PasswordLabel','סיסמא')}</label>
            <input id="register-password" type="password" value={password} onChange={e => { setPassword(e.target.value); const err = e.target.value.length < 6 ? 'Password must be at least 6 characters' : null; setFieldErrors(prev => ({ ...prev, password: err ?? undefined })); }} required aria-describedby={fieldErrors.password ? 'register-password-error' : undefined} aria-invalid={!!fieldErrors.password} />
            {fieldErrors.password ? <div id="register-password-error" className="error-message">{fieldErrors.password}</div> : null}
          </div>
          <div className="form-group">
            <label htmlFor="register-confirm-password">{pageRes['Register.ConfirmPasswordLabel'] ?? resources.get('Register.ConfirmPasswordLabel','אשר סיסמא')}</label>
            <input id="register-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required aria-required="true" />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? (pageRes['Register.Submit'] ?? resources.get('Register.Submit','הרשמה') + '...') : (pageRes['Register.Submit'] ?? resources.get('Register.Submit','הרשמה'))}</button>
        </form>
      )}
    </div>
  );
};

export default Register;